import { BatchSourceInfo, ChatSource, DocSource, QASource } from '../types';

/**
 * Formats a complete response with sources appended at the bottom.
 * This is used for copy-to-clipboard functionality and the Complete Response column.
 * Note: Evidence is not included as it duplicates source information.
 */
export function formatCompleteResponse(
  response: string,
  sources: BatchSourceInfo[] | ChatSource[]
): string {
  let result = response;

  // Format and add sources (evidence is not included as it duplicates sources)
  if (sources && sources.length > 0) {
    const formattedSources = formatSourcesList(sources);
    if (formattedSources.length > 0) {
      result += '\n\nSources:';
      formattedSources.forEach(source => {
        result += `\n- ${source}`;
      });
    }
  }

  return result;
}

/**
 * Formats sources into a list of strings with format: "DocName (URL)" or just "DocName"
 */
export function formatSourcesList(sources: BatchSourceInfo[] | ChatSource[]): string[] {
  return sources.map(source => {
    // Handle BatchSourceInfo format
    if (typeof source === 'object' && 'doc_name' in source) {
      const docSource = source as BatchSourceInfo | DocSource;
      if (docSource.url) {
        return `${docSource.doc_name} (${docSource.url})`;
      }
      return docSource.doc_name;
    }
    
    // Handle QASource format
    if (typeof source === 'object' && 'type' in source && source.type === 'previous_qa') {
      const qaSource = source as QASource;
      return `Previous Q&A (${qaSource.client})`;
    }
    
    // Handle string format
    if (typeof source === 'string') {
      return source;
    }
    
    return JSON.stringify(source);
  }).filter(s => s);
}

/**
 * Copies text to clipboard and returns a promise
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
      document.body.removeChild(textArea);
      return true;
    } catch (e) {
      document.body.removeChild(textArea);
      return false;
    }
  }
}

